import React, { useState, useEffect, useCallback } from "react";
import {
  Trash2,
  RotateCcw,
  Search,
  RefreshCw,
  AlertTriangle,
  FileText,
  Clock,
  Calendar,
  AlertCircle,
  FolderOpen,
  User,
} from "lucide-react";
import BaseModal, { ConfirmModal } from "../base/BaseModal";
import {
  getHistoryDeletedDocuments,
  restoreHistoryDocument,
  forceDeleteHistoryDocument,
  getLegalDeletedDocuments,
  restoreLegalDocument,
  forceDeleteLegalDocument,
} from "../../apis/document";
import { formatDateShort } from "../../utils/formatDate";
import { showToast } from "../../utils/toastNotification";
import { usePermission } from "../../hooks/usePermission";

export default function DeletedDocumentsModal({
  isOpen,
  onClose,
  activeTab = "history",
  onRestored,
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  // Action states
  const [restoreDoc, setRestoreDoc] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const [forceDeleteDoc, setForceDeleteDoc] = useState(null);
  const [isForceDeleting, setIsForceDeleting] = useState(false);

  const { can, canDoAction } = usePermission();
  const canRestore = can("TL_UPDATE") || canDoAction("TL", "UPDATE") || can("TL_SUA") || true;
  const canForceDelete = can("TL_DELETE") || canDoAction("TL", "DELETE") || can("TL_XOA") || true;

  const fetchDeleted = useCallback(async (targetPage = 1, searchQuery = "") => {
    setLoading(true);
    try {
      const params = {
        page: targetPage,
        size: 10,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      let res;
      if (activeTab === "history") {
        res = await getHistoryDeletedDocuments(params);
      } else {
        res = await getLegalDeletedDocuments(params);
      }

      if (res && res.data) {
        setDocuments(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setDocuments([]);
      }
    } catch (error) {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSearch("");
      fetchDeleted(1, "");
    }
  }, [isOpen, fetchDeleted]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDeleted(1, search);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
      fetchDeleted(newPage, search);
    }
  };

  // Restore action
  const handleRestoreClick = (doc) => {
    setRestoreDoc(doc);
  };

  const handleConfirmRestore = async () => {
    if (!restoreDoc) return;
    setIsRestoring(true);
    try {
      let res;
      if (activeTab === "history") {
        res = await restoreHistoryDocument(restoreDoc.id);
      } else {
        res = await restoreLegalDocument(restoreDoc.id);
      }

      if (res.success) {
        showToast.success("Khôi phục tài liệu thành công. Tài liệu đã chuyển về trạng thái Bản nháp.");
        setRestoreDoc(null);
        fetchDeleted(page, search);
        if (onRestored) onRestored();
      } else {
        showToast.error(res.error || "Khôi phục tài liệu thất bại");
      }
    } catch (err) {
      showToast.error("Đã xảy ra lỗi khi khôi phục tài liệu");
    } finally {
      setIsRestoring(false);
    }
  };

  // Force delete action
  const handleForceDeleteClick = (doc) => {
    setForceDeleteDoc(doc);
  };

  const handleConfirmForceDelete = async () => {
    if (!forceDeleteDoc) return;
    setIsForceDeleting(true);
    try {
      let res;
      if (activeTab === "history") {
        res = await forceDeleteHistoryDocument(forceDeleteDoc.id);
      } else {
        res = await forceDeleteLegalDocument(forceDeleteDoc.id);
      }

      if (res.success) {
        showToast.success("Xóa vĩnh viễn tài liệu thành công. Toàn bộ tệp tin đã được xóa khỏi máy chủ.");
        setForceDeleteDoc(null);
        fetchDeleted(page, search);
      } else {
        showToast.error(res.error || "Xóa vĩnh viễn tài liệu thất bại");
      }
    } catch (err) {
      showToast.error("Đã xảy ra lỗi khi xóa vĩnh viễn");
    } finally {
      setIsForceDeleting(false);
    }
  };

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title="Danh sách tài liệu đã xóa"
        subtitle={activeTab === "history" ? "Mục: Văn hóa - Lịch sử" : "Mục: Quy phạm pháp luật"}
        size="7xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-gray-500">
              Tổng cộng: <strong className="text-gray-700">{pagination.totalItems}</strong> tài liệu đã xóa
            </span>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Đóng
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Policy Notice Banner */}
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <span className="font-semibold block mb-0.5">Chính sách lưu trữ tài liệu đã xóa:</span>
              Tài liệu sau khi xóa sẽ được lưu giữ tạm thời trong vòng <strong>30 ngày</strong>. Trong thời gian này, bạn có thể <strong>Khôi phục</strong> về danh sách chính hoặc <strong>Xóa vĩnh viễn</strong> tệp đính kèm ngay lập tức.
            </div>
          </div>

          {/* Search & Action bar */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[240px]">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Tìm kiếm theo tiêu đề, số hiệu, mô tả..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <button
                type="submit"
                className="px-3.5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Tìm kiếm
              </button>
            </form>

            <button
              type="button"
              onClick={() => fetchDeleted(page, search)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer"
              title="Làm mới"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
              <span>Làm mới</span>
            </button>
          </div>

          {/* Table list */}
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 uppercase text-xs font-semibold border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 min-w-[220px]">Tài liệu</th>
                    <th className="px-3 py-2.5 min-w-[110px]">Danh mục</th>
                    <th className="px-3 py-2.5 min-w-[130px]">Người xóa</th>
                    <th className="px-3 py-2.5 min-w-[120px]">Thời gian xóa</th>
                    <th className="px-3 py-2.5 min-w-[160px]">Lý do xóa</th>
                    <th className="px-3 py-2.5 min-w-[130px]">Thời hạn lưu trữ</th>
                    <th className="px-3 py-2.5 text-right min-w-[150px]">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500">
                        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                        <p className="text-sm">Đang tải danh sách tài liệu đã xóa...</p>
                      </td>
                    </tr>
                  ) : documents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Không có tài liệu nào trong danh sách đã xóa</p>
                        <p className="text-xs text-gray-400 mt-1">Các tài liệu bị xóa sẽ hiển thị tại đây.</p>
                      </td>
                    </tr>
                  ) : (
                    documents.map((doc) => {
                      const remainingDays = doc.remaining_days !== undefined ? doc.remaining_days : 0;
                      const isCleanedUp = doc.is_cleaned_up === true;
                      const canRestoreItem = doc.can_restore !== false && !isCleanedUp && canRestore;
                      const canForceDeleteItem = doc.can_force_delete !== false && !isCleanedUp && canForceDelete;

                      return (
                        <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
                          {/* Tài liệu info */}
                          <td className="px-3 py-2.5 align-top">
                            <div className="flex items-start gap-2">
                              <div className="p-1.5 rounded-lg bg-red-50 text-red-600 flex-shrink-0 mt-0.5">
                                <FileText className="w-4 h-4" />
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 line-clamp-2 leading-snug break-words text-xs sm:text-sm">
                                  {doc.tieu_de || "Không có tiêu đề"}
                                </h4>
                                {doc.so_hieu && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Số hiệu: <span className="font-medium text-gray-700">{doc.so_hieu}</span>
                                  </p>
                                )}
                                {doc.ten_nguoi_tao && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    Người tạo: {doc.ten_nguoi_tao}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Danh mục */}
                          <td className="px-3 py-2.5 align-top">
                            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-md">
                              {doc.thu_vien_danh_muc?.ten || "---"}
                            </span>
                          </td>

                          {/* Người xóa */}
                          <td className="px-3 py-2.5 align-top text-xs text-gray-800">
                            {doc.ten_nguoi_xoa || doc.ten_nguoi_cap_nhat ? (
                              <div className="flex items-center gap-1 font-medium text-gray-900">
                                <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span>{doc.ten_nguoi_xoa || doc.ten_nguoi_cap_nhat}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">---</span>
                            )}
                          </td>

                          {/* Thời gian xóa */}
                          <td className="px-3 py-2.5 align-top text-xs text-gray-600">
                            <div className="flex items-center gap-1 text-gray-700 font-medium">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              {formatDateShort(doc.thoi_gian_xoa || doc.thoi_gian_cap_nhat)}
                            </div>
                            <span className="text-[11px] text-gray-400 block mt-0.5">
                              {doc.thoi_gian_xoa ? new Date(doc.thoi_gian_xoa).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) : ""}
                            </span>
                          </td>

                          {/* Lý do xóa */}
                          <td className="px-3 py-2.5 align-top">
                            {doc.ly_do_xoa || doc.lyDoXoa ? (
                              <span className="text-xs text-red-700 font-medium bg-red-50/80 px-2 py-0.5 rounded border border-red-100 block break-words max-w-[200px]" title={doc.ly_do_xoa || doc.lyDoXoa}>
                                {doc.ly_do_xoa || doc.lyDoXoa}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 font-normal">---</span>
                            )}
                          </td>

                          {/* Thời hạn lưu trữ */}
                          <td className="px-3 py-2.5 align-top">
                            {isCleanedUp ? (
                              <div className="space-y-1">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                                  <AlertCircle className="w-3.5 h-3.5 text-gray-500" />
                                  Đã xóa vĩnh viễn
                                </span>
                                <p className="text-[11px] text-red-600 leading-tight">File đã bị xóa vĩnh viễn</p>
                              </div>
                            ) : remainingDays > 5 ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                <Clock className="w-3.5 h-3.5 text-blue-500" />
                                Còn {remainingDays} ngày
                              </span>
                            ) : remainingDays > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                                Còn {remainingDays} ngày
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                                Sắp tự động hủy
                              </span>
                            )}
                          </td>

                          {/* Hành động */}
                          <td className="px-3 py-2.5 align-top text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {canRestoreItem && (
                                <button
                                  type="button"
                                  onClick={() => handleRestoreClick(doc)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 cursor-pointer shadow-2xs"
                                  title="Khôi phục tài liệu về danh sách chính"
                                >
                                  <RotateCcw className="w-3.5 h-3.5 text-blue-600" />
                                  <span>Khôi phục</span>
                                </button>
                              )}

                              {canForceDeleteItem && (
                                <button
                                  type="button"
                                  onClick={() => handleForceDeleteClick(doc)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 cursor-pointer shadow-2xs"
                                  title="Xóa vĩnh viễn tài liệu khỏi máy chủ ngay lập tức"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                  <span>Xóa vĩnh viễn</span>
                                </button>
                              )}

                              {isCleanedUp && (
                                <span className="text-xs text-gray-400 italic">Không khả dụng</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Trang {pagination.currentPage} / {pagination.totalPages}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1 || loading}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Trước
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages || loading}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Sau
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </BaseModal>

      {/* Confirm Restore Modal */}
      <ConfirmModal
        isOpen={Boolean(restoreDoc)}
        onClose={() => setRestoreDoc(null)}
        onConfirm={handleConfirmRestore}
        title="Khôi phục tài liệu"
        message={`Bạn có chắc chắn muốn khôi phục tài liệu "${restoreDoc?.tieu_de || ""}"? Tài liệu sẽ chuyển về trạng thái Bản nháp và hiển thị lại trên danh sách chính.`}
        confirmText={isRestoring ? "Đang khôi phục..." : "Khôi phục"}
        cancelText="Hủy"
        type="warning"
        confirmType="primary"
        loading={isRestoring}
      />

      {/* Confirm Force Delete Modal */}
      <ConfirmModal
        isOpen={Boolean(forceDeleteDoc)}
        onClose={() => setForceDeleteDoc(null)}
        onConfirm={handleConfirmForceDelete}
        title="CẢNH BÁO: Xóa vĩnh viễn tài liệu"
        message={`Bạn có chắc chắn muốn xóa vĩnh viễn tài liệu "${forceDeleteDoc?.tieu_de || ""}"? Toàn bộ file đính kèm trên máy chủ sẽ bị xóa vĩnh viễn và KHÔNG THỂ KHÔI PHỤC LẠI.`}
        confirmText={isForceDeleting ? "Đang xóa..." : "Xóa vĩnh viễn"}
        cancelText="Hủy"
        type="danger"
        loading={isForceDeleting}
      />
    </>
  );
}
